"use client";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import RoleGuard from "@/core/auth/components/role-guard";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  addCategory,
  AddCategorySchema,
  AddCategorySchemaType,
} from "@/services/addCategory";

export default function AddCategory() {
  const { user } = useAuth();

  const addCategoryForm = useForm<AddCategorySchemaType>({
    resolver: zodResolver(AddCategorySchema),
    defaultValues: {
      name: undefined,
      description: "",
    },
  });
  const queryClient = useQueryClient();


  const navigate = useNavigate();

  const handleAddCategoryMutation = useMutation({
    mutationFn: async (formData: AddCategorySchemaType) => {
      const payload = {
        name: formData.name,
        description: formData.description,
      };
      
      return addCategory(payload);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category added successfully");
      navigate("/app/categories");
    },
    onError: (error) => {
      const newError = error as any;
      console.error(
        "An error occurred while adding the category:",
        newError.response?.data?.message
      );
      toast.error(newError.response?.data?.message ?? "An error occured");
    },
  });

  const { isPending } = handleAddCategoryMutation;

  const onSubmit = (values: AddCategorySchemaType) => {
    handleAddCategoryMutation.mutate(values);
  };

  return (
    <RoleGuard requiredRoles={["super-admin"]}>
      <div className="w-full p-6 min-h-screen">
        <Link
          type="button"
          to="/app/categories"
          className={`${buttonVariants({ variant: "outline" })}`}
        >
          <ChevronLeft className="h-4 w-4" />
        </Link>
        <div className="mb-4 max-w-4xl mx-auto">
          

          <Form {...addCategoryForm}>
            <form onSubmit={addCategoryForm.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-6 p-6 border rounded-md">
                <div className="w-full">
                  <FormField
                    control={addCategoryForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category Name</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a category type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Primary Packages">
                                Primary Packages
                              </SelectItem>
                              <SelectItem value="Addons">
                                Addons
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="w-full">
                  <FormField
                    control={addCategoryForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Input placeholder="description" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="w-full"></div>
                <div className="">
                  <Button type="submit" className="py-2 rounded-md">
                    {isPending ? "Adding Category..." : "Add Category"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </RoleGuard>
  );
}
